/**
 * MagicBI Design System - Design Tokens Index
 * 
 * Central export for all design tokens including colors, typography,
 * spacing, shadows, borders, transitions, and breakpoints.
 */

// Import all tokens for the design tokens object
import {
  rawColors as importedRawColors,
  lightThemeColors as importedLightThemeColors,
  darkThemeColors as importedDarkThemeColors,
} from './colors';

import {
  typography as importedTypography,
} from './typography';

import {
  spacing as importedSpacing,
  semanticSpacing as importedSemanticSpacing,
  componentSpacing as importedComponentSpacing,
  layoutSpacing as importedLayoutSpacing,
} from './spacing';

import {
  shadows as importedShadows,
  darkShadows as importedDarkShadows,
  effectShadows as importedEffectShadows,
  zIndex as importedZIndex,
} from './shadows';

import {
  borderRadius as importedBorderRadius,
  borderWidth as importedBorderWidth,
  borderStyle as importedBorderStyle,
  semanticBorderRadius as importedSemanticBorderRadius,
  componentBorders as importedComponentBorders,
  borderColors as importedBorderColors,
} from './borders';

import {
  duration as importedDuration,
  easing as importedEasing,
  transitions as importedTransitions,
  animations as importedAnimations,
} from './transitions';

import {
  breakpointValues as importedBreakpointValues,
  mediaQueries as importedMediaQueries,
  deviceQueries as importedDeviceQueries,
  containerMaxWidths as importedContainerMaxWidths,
  gridSystem as importedGridSystem,
  responsiveMixin as importedResponsiveMixin,
} from './breakpoints';

import { homePageTokens as importedHomePageTokens } from './home';

// Export all color tokens
export {
  rawColors,
  rawHex,
  lightThemeColors,
  darkThemeColors,
  type ColorTokens,
  type ColorPalette,
  type ColorShade,
} from './colors';

export { darkRawColors } from './darkRawColors';

// Export all typography tokens
export {
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  letterSpacing,
  typography,
  responsiveTypography,
  getTypographyStyle,
  typographyMixin,
  type TypographyTokens,
  type TypographyCategory,
  type TypographyVariant,
} from './typography';

// Export all spacing tokens
export {
  spacing,
  semanticSpacing,
  componentSpacing,
  layoutSpacing,
  getSpacing,
  getSemanticSpacing,
  spacingMixin,
  type Spacing,
  type SpacingKey,
  type SemanticSpacing,
  type ComponentSpacing,
  type LayoutSpacing,
} from './spacing';

// Export all shadow tokens
export {
  shadows,
  darkShadows,
  effectShadows,
  zIndex,
  getShadow,
  type ShadowTokens,
} from './shadows';

// Export all border tokens
export {
  borderRadius,
  borderWidth,
  borderStyle,
  semanticBorderRadius,
  componentBorders,
  borderColors,
  getBorderRadius,
  type BorderRadius,
  type BorderWidth,
  type BorderStyle,
  type SemanticBorderRadius,
  type ComponentBorders,
  type BorderColors,
} from './borders';

// Export all transition tokens
export {
  duration,
  easing,
  transitions,
  animations,
  type Duration,
  type Easing,
  type Transitions,
  type Animations,
} from './transitions';

export {
  PAGE_TRANSITION_MOTION,
  PAGE_TRANSITION_SECTION_RULES,
  pageTransitionVariants,
  resolvePageTransitionKey,
  type PageTransitionScope,
  type PageTransitionSectionRule,
} from './pageTransitionMotion';

// Export feature-level Home V2 tokens
export { homePageTokens, type HomePageTokens } from './home';

// Export control size tokens
export {
  controlSize,
  headerMaxWidth,
  type ControlCategory,
  type ControlSizeKey,
} from './controlSizes';

// Export all breakpoint tokens
export {
  breakpointValues,
  mediaQueries,
  deviceQueries,
  containerMaxWidths,
  gridSystem,
  responsiveMixin,
  type Breakpoint,
  type BreakpointValues,
  type MediaQueries,
  type DeviceQueries,
  type ResponsiveValue,
} from './breakpoints';

// Create design token collections using imported tokens
export const designTokens = {
  colors: {
    light: importedLightThemeColors,
    dark: importedDarkThemeColors,
    raw: importedRawColors,
  },
  typography: importedTypography,
  spacing: {
    base: importedSpacing,
    semantic: importedSemanticSpacing,
    component: importedComponentSpacing,
    layout: importedLayoutSpacing,
  },
  shadows: {
    light: importedShadows,
    dark: importedDarkShadows,
    effects: importedEffectShadows,
  },
  borders: {
    radius: importedBorderRadius,
    width: importedBorderWidth,
    style: importedBorderStyle,
    semantic: importedSemanticBorderRadius,
    component: importedComponentBorders,
    colors: importedBorderColors,
  },
  transitions: {
    duration: importedDuration,
    easing: importedEasing,
    semantic: importedTransitions,
    animations: importedAnimations,
  },
  breakpoints: {
    values: importedBreakpointValues,
    mediaQueries: importedMediaQueries,
    deviceQueries: importedDeviceQueries,
    containerMaxWidths: importedContainerMaxWidths,
    gridSystem: importedGridSystem,
    responsiveMixin: importedResponsiveMixin,
  },
  features: {
    home: importedHomePageTokens,
  },
  zIndex: importedZIndex,
} as const;

// Export the design tokens collection
export default designTokens;
